import type { ImageSourcePropType } from "react-native";
import type { ProcedureBody } from "@src/packages/content";
import appointmentIllustration from "../../../assets/images/document-review-icons/monitoring-appointment-3d.png";
import cryoVaultIllustration from "../../../assets/images/cryo-vault-fridge.png";
import embryoGeneticsIllustration from "../../../assets/images/embryo-genetics.png";
import eggBasketIllustration from "../../../assets/images/egg-basket.png";
import eggRetrievalIllustration from "../../../assets/images/egg-retrieval.png";
import embryoTransferIllustration from "../../../assets/images/embryo-transfer.png";
import endometrialBiopsyCollectionIllustration from "../../../assets/images/endometrial-biopsy-collection.png";
import endometrialLiningIllustration from "../../../assets/images/endometrial-lining.png";
import expandedCarrierScreeningIllustration from "../../../assets/images/expanded-carrier-screening.png";
import fertilityHormoneTrackerIllustration from "../../../assets/images/fertility-hormone-tracker.png";
import follicleCountIllustration from "../../../assets/images/follicle-count.png";
import hysteroscopyIllustration from "../../../assets/images/hysteroscopy.png";
import injectionPenIllustration from "../../../assets/images/injection-pen.png";
import karyotypeIllustration from "../../../assets/images/karyotype.png";
import pelvicMriIllustration from "../../../assets/images/pelvic-mri.png";
import spermCollectionIllustration from "../../../assets/images/sperm-collection.png";
import testTubesIllustration from "../../../assets/images/you-icons/test-tubes.png";
import ultrasoundProbeIllustration from "../../../assets/images/ultrasound-probe.png";
import wholeGenomeSequencingIllustration from "../../../assets/images/whole-genome-sequencing.png";

type ProcedureIllustrationInput = {
  procedureId: string;
  category?: ProcedureBody["category"];
};

const ULTRASOUND_PROCEDURES = new Set([
  "proc-doppler-ultrasound",
  "proc-hsg",
  "procedure-hsg",
  "proc-hysterosonography",
  "proc-transabdominal-ultrasound",
  "proc-transvaginal-ultrasound",
]);

const GENETIC_PROCEDURES = new Set([
  "proc-assisted-hatching",
  "proc-embryo-biopsy-pgt",
  "proc-embryo-culture",
  "proc-fertilization-icsi",
]);

const TRANSFER_PROCEDURES = new Set([
  "proc-embryo-transfer",
  "proc-frozen-embryo-transfer",
  "proc-iui",
  "proc-mock-transfer",
]);

const SPERM_PROCEDURES = new Set([
  "proc-sperm-collection",
  "proc-sperm-freeze",
  "proc-surgical-sperm-retrieval",
  "proc-tese",
  "proc-testicular-biopsy",
]);

const SURGICAL_PROCEDURES = new Set(["proc-hysteroscopy", "proc-laparoscopy"]);

const ENDOMETRIAL_PROCEDURES = new Set([
  "proc-endometrial-scratch",
  "proc-myomectomy",
  "proc-polypectomy",
]);

const CONSULTATION_PROCEDURES = new Set([
  "proc-follow-up-consult",
  "proc-genetic-counseling",
  "proc-initial-consult",
  "proc-two-week-wait",
]);

const STIMULATION_PROCEDURES = new Set([
  "proc-endometrial-preparation",
  "proc-luteal-support",
  "proc-mini-ivf",
  "proc-ovarian-stimulation",
  "proc-trigger-shot",
]);

const BLOOD_TEST_PROCEDURES = new Set([
  "proc-hormonal-testing",
  "proc-thyroid-testing",
]);

const SAMPLE_TEST_PROCEDURES = new Set([
  "proc-pap-smear",
  "proc-std-testing",
  "proc-swab-test",
  "proc-urinalysis",
]);

export function getProcedureIllustration({
  procedureId,
  category,
}: ProcedureIllustrationInput): ImageSourcePropType {
  if (procedureId === "proc-endometrial-biopsy") {
    return endometrialBiopsyCollectionIllustration;
  }

  if (procedureId === "proc-antral-follicle-count") {
    return follicleCountIllustration;
  }

  if (procedureId === "proc-carrier-testing") {
    return expandedCarrierScreeningIllustration;
  }

  if (procedureId === "proc-exome-genome-sequencing") {
    return wholeGenomeSequencingIllustration;
  }

  if (procedureId === "proc-karyotyping") {
    return karyotypeIllustration;
  }

  if (procedureId === "proc-pelvic-mri") {
    return pelvicMriIllustration;
  }

  if (procedureId === "proc-egg-retrieval") {
    return eggRetrievalIllustration;
  }

  if (
    procedureId === "proc-cryopreservation" ||
    procedureId === "proc-sperm-freeze"
  ) {
    return cryoVaultIllustration;
  }

  if (ENDOMETRIAL_PROCEDURES.has(procedureId)) {
    return endometrialLiningIllustration;
  }

  if (TRANSFER_PROCEDURES.has(procedureId)) {
    return embryoTransferIllustration;
  }

  if (SPERM_PROCEDURES.has(procedureId)) {
    return spermCollectionIllustration;
  }

  if (SURGICAL_PROCEDURES.has(procedureId)) {
    return hysteroscopyIllustration;
  }

  if (ULTRASOUND_PROCEDURES.has(procedureId)) {
    return ultrasoundProbeIllustration;
  }

  if (GENETIC_PROCEDURES.has(procedureId)) {
    return embryoGeneticsIllustration;
  }

  if (STIMULATION_PROCEDURES.has(procedureId)) {
    return injectionPenIllustration;
  }

  if (BLOOD_TEST_PROCEDURES.has(procedureId)) {
    return fertilityHormoneTrackerIllustration;
  }

  if (SAMPLE_TEST_PROCEDURES.has(procedureId)) {
    return testTubesIllustration;
  }

  if (CONSULTATION_PROCEDURES.has(procedureId)) {
    return appointmentIllustration;
  }

  if (category === "recovery") {
    return eggBasketIllustration;
  }

  return category === "transfer"
    ? embryoTransferIllustration
    : category === "retrieval"
      ? eggRetrievalIllustration
      : category === "stimulation"
        ? injectionPenIllustration
        : category === "consultation"
          ? appointmentIllustration
          : category === "lab" || category === "diagnostic"
            ? testTubesIllustration
            : ultrasoundProbeIllustration;
}
